import { useState } from 'react';
import { BookOpen, AlertTriangle, CheckCircle, Clock, Search, Filter, Plus, ExternalLink, UserCheck, ShoppingCart } from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';

/**
 * Scenario Playbook Interface
 * Pre-defined response procedures with actions for people and third parties
 */
interface PlaybookAction {
  id: string;
  step: number;
  title: string;
  description: string;
  assignedTo: string; // Person or third party
  assigneeType: 'Internal' | 'Third-Party';
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  estimatedDuration: string;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Blocked';
  dependencies?: string[]; // Step IDs that must complete first
}

interface ScenarioPlaybook {
  id: string;
  name: string;
  category: 'Cyber Security' | 'Data Breach' | 'System Outage' | 'Compliance' | 'Third-Party' | 'Business Continuity';
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'Active' | 'Draft' | 'Under Review' | 'Archived';
  description: string;
  
  // Playbook Metadata
  owner: string;
  lastActivated?: string;
  totalExecutions: number;
  avgResponseTime: string;
  
  // Actions
  actions: PlaybookAction[];
  
  // Connected Entities
  connectedProcesses: string[];
  connectedRisks: string[];
  connectedSuppliers: string[];
  
  // Compliance
  complianceFrameworks: string[];
  lastReviewed: string;
  nextReview: string;
}

const mockPlaybooks: ScenarioPlaybook[] = [
  {
    id: 'PB001',
    name: 'Ransomware Attack Response',
    category: 'Cyber Security',
    severity: 'Critical',
    status: 'Active',
    description: 'Comprehensive response procedure for ransomware incidents including containment, recovery, and law enforcement coordination',
    owner: 'David Kim',
    lastActivated: '2025-08-15',
    totalExecutions: 3,
    avgResponseTime: '4.5 hours',
    actions: [
      {
        id: 'A001',
        step: 1,
        title: 'Immediate Network Isolation',
        description: 'Disconnect affected systems from network to prevent lateral spread of ransomware',
        assignedTo: 'David Kim (IT Security)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '15 minutes',
        status: 'Completed',
      },
      {
        id: 'A002',
        step: 2,
        title: 'Activate Incident Response Team',
        description: 'Notify all IR team members and initiate war room protocols',
        assignedTo: 'Sarah Mitchell (Risk Manager)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '30 minutes',
        status: 'Completed',
        dependencies: ['A001'],
      },
      {
        id: 'A003',
        step: 3,
        title: 'Engage Cyber Security Vendor',
        description: 'Contact CrowdStrike for forensic analysis and containment support',
        assignedTo: 'CrowdStrike (EDR Provider)',
        assigneeType: 'Third-Party',
        priority: 'Critical',
        estimatedDuration: '1 hour',
        status: 'In Progress',
        dependencies: ['A001', 'A002'],
      },
      {
        id: 'A004',
        step: 4,
        title: 'Forensic Data Collection',
        description: 'Preserve logs, memory dumps, and disk images for investigation',
        assignedTo: 'Michael Chen (Controls Specialist)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '2 hours',
        status: 'In Progress',
        dependencies: ['A003'],
      },
      {
        id: 'A005',
        step: 5,
        title: 'Notify Regulatory Authorities',
        description: 'Report incident to relevant regulators per DORA requirements (within 24 hours)',
        assignedTo: 'James Rodriguez (Compliance)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '3 hours',
        status: 'Not Started',
        dependencies: ['A004'],
      },
      {
        id: 'A006',
        step: 6,
        title: 'Law Enforcement Coordination',
        description: 'File report with FBI Cyber Division and local law enforcement',
        assignedTo: 'External Legal Counsel',
        assigneeType: 'Third-Party',
        priority: 'High',
        estimatedDuration: '4 hours',
        status: 'Not Started',
        dependencies: ['A004'],
      },
      {
        id: 'A007',
        step: 7,
        title: 'Backup Restoration Planning',
        description: 'Assess backup integrity and plan recovery sequence',
        assignedTo: 'AWS (Cloud Backup)',
        assigneeType: 'Third-Party',
        priority: 'High',
        estimatedDuration: '2 hours',
        status: 'Not Started',
        dependencies: ['A003', 'A004'],
      },
      {
        id: 'A008',
        step: 8,
        title: 'Customer Communication',
        description: 'Prepare and distribute customer notification per SLA requirements',
        assignedTo: 'Olivia Martinez (Customer Success)',
        assigneeType: 'Internal',
        priority: 'Medium',
        estimatedDuration: '2 hours',
        status: 'Not Started',
        dependencies: ['A005'],
      },
    ],
    connectedProcesses: ['P003: Incident Response', 'P008: Business Continuity Testing'],
    connectedRisks: ['R005: Cyber Security Risk', 'R009: Data Breach Risk'],
    connectedSuppliers: ['CrowdStrike (EDR)', 'AWS (Cloud Backup)', 'External Legal Counsel'],
    complianceFrameworks: ['DORA', 'NIS2', 'GDPR', 'ISO 27001'],
    lastReviewed: '2025-07-20',
    nextReview: '2026-01-20',
  },
  {
    id: 'PB002',
    name: 'Data Breach Response',
    category: 'Data Breach',
    severity: 'Critical',
    status: 'Active',
    description: 'GDPR-compliant data breach response including notification, containment, and remediation',
    owner: 'James Rodriguez',
    lastActivated: '2025-06-10',
    totalExecutions: 2,
    avgResponseTime: '6 hours',
    actions: [
      {
        id: 'B001',
        step: 1,
        title: 'Breach Assessment',
        description: 'Determine scope, affected data, and number of individuals impacted',
        assignedTo: 'Sarah Mitchell (Risk Manager)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '1 hour',
        status: 'Completed',
      },
      {
        id: 'B002',
        step: 2,
        title: 'Containment Actions',
        description: 'Secure affected systems and prevent further data exposure',
        assignedTo: 'David Kim (IT Security)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '2 hours',
        status: 'Completed',
        dependencies: ['B001'],
      },
      {
        id: 'B003',
        step: 3,
        title: 'Engage Legal Counsel',
        description: 'Consult legal team on regulatory obligations and disclosure requirements',
        assignedTo: 'External Legal Counsel',
        assigneeType: 'Third-Party',
        priority: 'Critical',
        estimatedDuration: '2 hours',
        status: 'In Progress',
        dependencies: ['B001'],
      },
      {
        id: 'B004',
        step: 4,
        title: 'Regulatory Notification (72 hours)',
        description: 'Notify DPA/supervisory authority within GDPR 72-hour requirement',
        assignedTo: 'James Rodriguez (Compliance)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '4 hours',
        status: 'Not Started',
        dependencies: ['B001', 'B003'],
      },
      {
        id: 'B005',
        step: 5,
        title: 'Affected Individual Notification',
        description: 'Prepare and send breach notifications to impacted customers',
        assignedTo: 'Olivia Martinez (Customer Success)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '6 hours',
        status: 'Not Started',
        dependencies: ['B003', 'B004'],
      },
      {
        id: 'B006',
        step: 6,
        title: 'Credit Monitoring Setup',
        description: 'Arrange identity protection services for affected individuals',
        assignedTo: 'Experian (Credit Monitoring)',
        assigneeType: 'Third-Party',
        priority: 'High',
        estimatedDuration: '8 hours',
        status: 'Not Started',
        dependencies: ['B004'],
      },
      {
        id: 'B007',
        step: 7,
        title: 'Root Cause Analysis',
        description: 'Conduct investigation to identify breach origin and vulnerabilities',
        assignedTo: 'Michael Chen (Controls Specialist)',
        assigneeType: 'Internal',
        priority: 'Medium',
        estimatedDuration: '1 week',
        status: 'Not Started',
        dependencies: ['B002'],
      },
    ],
    connectedProcesses: ['P003: Incident Response', 'P004: Regulatory Reporting'],
    connectedRisks: ['R009: Data Breach Risk', 'R002: Compliance Risk'],
    connectedSuppliers: ['External Legal Counsel', 'Experian (Credit Monitoring)'],
    complianceFrameworks: ['GDPR', 'DORA', 'CCPA'],
    lastReviewed: '2025-08-01',
    nextReview: '2026-02-01',
  },
  {
    id: 'PB003',
    name: 'Critical System Outage',
    category: 'System Outage',
    severity: 'High',
    status: 'Active',
    description: 'Response procedure for critical system failures affecting customer-facing services',
    owner: 'Emma Thompson',
    lastActivated: '2025-09-05',
    totalExecutions: 5,
    avgResponseTime: '2.5 hours',
    actions: [
      {
        id: 'C001',
        step: 1,
        title: 'Incident Declaration',
        description: 'Declare major incident and activate response team',
        assignedTo: 'Emma Thompson (BCP Manager)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '10 minutes',
        status: 'Completed',
      },
      {
        id: 'C002',
        step: 2,
        title: 'Customer Status Page Update',
        description: 'Post initial incident notification on status page',
        assignedTo: 'Olivia Martinez (Customer Success)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '15 minutes',
        status: 'Completed',
        dependencies: ['C001'],
      },
      {
        id: 'C003',
        step: 3,
        title: 'Engage AWS Support',
        description: 'Open Priority 1 support ticket with AWS for infrastructure issues',
        assignedTo: 'AWS (Cloud Infrastructure)',
        assigneeType: 'Third-Party',
        priority: 'Critical',
        estimatedDuration: '30 minutes',
        status: 'In Progress',
        dependencies: ['C001'],
      },
      {
        id: 'C004',
        step: 4,
        title: 'Failover to DR Environment',
        description: 'Initiate failover procedures to disaster recovery datacenter',
        assignedTo: 'David Kim (IT Security)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '1 hour',
        status: 'In Progress',
        dependencies: ['C003'],
      },
      {
        id: 'C005',
        step: 5,
        title: 'Enterprise Customer Calls',
        description: 'Direct outreach to Enterprise customers per SLA requirements',
        assignedTo: 'Sarah Mitchell (Account Manager)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '2 hours',
        status: 'Not Started',
        dependencies: ['C002'],
      },
      {
        id: 'C006',
        step: 6,
        title: 'Root Cause Investigation',
        description: 'Work with AWS to identify system failure cause',
        assignedTo: 'AWS (Cloud Infrastructure)',
        assigneeType: 'Third-Party',
        priority: 'Medium',
        estimatedDuration: '4 hours',
        status: 'Not Started',
        dependencies: ['C004'],
      },
    ],
    connectedProcesses: ['P008: Business Continuity Testing', 'P009: Customer Support Escalation'],
    connectedRisks: ['R006: Business Continuity Risk', 'R010: Operational Risk'],
    connectedSuppliers: ['AWS (Cloud Infrastructure)'],
    complianceFrameworks: ['ISO 22301', 'DORA'],
    lastReviewed: '2025-06-10',
    nextReview: '2025-12-10',
  },
  {
    id: 'PB004',
    name: 'Third-Party Vendor Failure',
    category: 'Third-Party',
    severity: 'High',
    status: 'Active',
    description: 'Response to critical vendor service disruption or vendor compromise',
    owner: 'Sarah Mitchell',
    totalExecutions: 1,
    avgResponseTime: '8 hours',
    actions: [
      {
        id: 'D001',
        step: 1,
        title: 'Vendor Impact Assessment',
        description: 'Identify affected processes and customer impact',
        assignedTo: 'Sarah Mitchell (Risk Manager)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '1 hour',
        status: 'Not Started',
      },
      {
        id: 'D002',
        step: 2,
        title: 'Activate Backup Vendor',
        description: 'Switch to alternate vendor per contingency plan',
        assignedTo: 'Emma Thompson (Operations)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '2 hours',
        status: 'Not Started',
        dependencies: ['D001'],
      },
      {
        id: 'D003',
        step: 3,
        title: 'Vendor Escalation',
        description: 'Escalate issue to vendor executive sponsor',
        assignedTo: 'Primary Vendor',
        assigneeType: 'Third-Party',
        priority: 'High',
        estimatedDuration: '1 hour',
        status: 'Not Started',
        dependencies: ['D001'],
      },
      {
        id: 'D004',
        step: 4,
        title: 'Regulatory Notification',
        description: 'Notify regulators of critical vendor disruption per DORA requirements',
        assignedTo: 'James Rodriguez (Compliance)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '3 hours',
        status: 'Not Started',
        dependencies: ['D001'],
      },
      {
        id: 'D005',
        step: 5,
        title: 'Customer Communication',
        description: 'Inform customers of potential service degradation',
        assignedTo: 'Olivia Martinez (Customer Success)',
        assigneeType: 'Internal',
        priority: 'Medium',
        estimatedDuration: '2 hours',
        status: 'Not Started',
        dependencies: ['D002'],
      },
    ],
    connectedProcesses: ['P007: Vendor Due Diligence', 'P009: Customer Support Escalation'],
    connectedRisks: ['R012: Third-Party Risk', 'R010: Operational Risk'],
    connectedSuppliers: ['All Critical Vendors'],
    complianceFrameworks: ['DORA', 'ISO 27001'],
    lastReviewed: '2025-10-01',
    nextReview: '2026-04-01',
  },
  {
    id: 'PB005',
    name: 'Regulatory Examination Response',
    category: 'Compliance',
    severity: 'High',
    status: 'Active',
    description: 'Coordinated response to regulatory examination or audit',
    owner: 'James Rodriguez',
    lastActivated: '2025-05-20',
    totalExecutions: 2,
    avgResponseTime: '5 days',
    actions: [
      {
        id: 'E001',
        step: 1,
        title: 'Examination Team Assembly',
        description: 'Assemble internal team and designate regulatory liaison',
        assignedTo: 'James Rodriguez (Compliance)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '1 day',
        status: 'Completed',
      },
      {
        id: 'E002',
        step: 2,
        title: 'Engage External Advisors',
        description: 'Retain regulatory counsel for examination support',
        assignedTo: 'KPMG (Compliance Advisory)',
        assigneeType: 'Third-Party',
        priority: 'High',
        estimatedDuration: '2 days',
        status: 'Completed',
        dependencies: ['E001'],
      },
      {
        id: 'E003',
        step: 3,
        title: 'Document Production',
        description: 'Compile and organize requested documentation',
        assignedTo: 'Sarah Mitchell (Risk Manager)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '3 days',
        status: 'In Progress',
        dependencies: ['E001'],
      },
      {
        id: 'E004',
        step: 4,
        title: 'Interview Preparation',
        description: 'Prepare staff for regulator interviews',
        assignedTo: 'KPMG (Compliance Advisory)',
        assigneeType: 'Third-Party',
        priority: 'Medium',
        estimatedDuration: '2 days',
        status: 'Not Started',
        dependencies: ['E002'],
      },
      {
        id: 'E005',
        step: 5,
        title: 'Executive Briefing',
        description: 'Brief senior management on examination status and findings',
        assignedTo: 'James Rodriguez (Compliance)',
        assigneeType: 'Internal',
        priority: 'Medium',
        estimatedDuration: '1 day',
        status: 'Not Started',
        dependencies: ['E003', 'E004'],
      },
    ],
    connectedProcesses: ['P004: Regulatory Reporting', 'P011: Policy Review & Attestation'],
    connectedRisks: ['R002: Compliance Risk', 'R007: Regulatory Reporting Risk'],
    connectedSuppliers: ['KPMG (Compliance Advisory)'],
    complianceFrameworks: ['DORA', 'Basel III', 'MiFID II'],
    lastReviewed: '2025-05-20',
    nextReview: '2026-05-20',
  },
  {
    id: 'PB006',
    name: 'Pandemic/Crisis Activation',
    category: 'Business Continuity',
    severity: 'Critical',
    status: 'Active',
    description: 'Business continuity plan activation for widespread crisis events',
    owner: 'Emma Thompson',
    lastActivated: '2020-03-15',
    totalExecutions: 1,
    avgResponseTime: '24 hours',
    actions: [
      {
        id: 'F001',
        step: 1,
        title: 'Crisis Management Team Activation',
        description: 'Convene crisis management team and declare BCP activation',
        assignedTo: 'Emma Thompson (BCP Manager)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '2 hours',
        status: 'Not Started',
      },
      {
        id: 'F002',
        step: 2,
        title: 'Remote Work Enablement',
        description: 'Scale VPN capacity and enable remote access for all staff',
        assignedTo: 'David Kim (IT Security)',
        assigneeType: 'Internal',
        priority: 'Critical',
        estimatedDuration: '4 hours',
        status: 'Not Started',
        dependencies: ['F001'],
      },
      {
        id: 'F003',
        step: 3,
        title: 'Vendor Status Check',
        description: 'Contact all critical vendors to assess their operational status',
        assignedTo: 'Sarah Mitchell (Risk Manager)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '8 hours',
        status: 'Not Started',
        dependencies: ['F001'],
      },
      {
        id: 'F004',
        step: 4,
        title: 'Cloud Capacity Scaling',
        description: 'Increase cloud infrastructure capacity for remote operations',
        assignedTo: 'AWS (Cloud Infrastructure)',
        assigneeType: 'Third-Party',
        priority: 'High',
        estimatedDuration: '4 hours',
        status: 'Not Started',
        dependencies: ['F002'],
      },
      {
        id: 'F005',
        step: 5,
        title: 'Regulatory Communication',
        description: 'Notify regulators of BCP activation and operational status',
        assignedTo: 'James Rodriguez (Compliance)',
        assigneeType: 'Internal',
        priority: 'High',
        estimatedDuration: '6 hours',
        status: 'Not Started',
        dependencies: ['F001'],
      },
      {
        id: 'F006',
        step: 6,
        title: 'Customer Reassurance Campaign',
        description: 'Communicate operational continuity to customer base',
        assignedTo: 'Olivia Martinez (Customer Success)',
        assigneeType: 'Internal',
        priority: 'Medium',
        estimatedDuration: '8 hours',
        status: 'Not Started',
        dependencies: ['F003'],
      },
    ],
    connectedProcesses: ['P008: Business Continuity Testing'],
    connectedRisks: ['R006: Business Continuity Risk', 'R012: Third-Party Risk'],
    connectedSuppliers: ['AWS (Cloud Infrastructure)', 'All Critical Vendors'],
    complianceFrameworks: ['ISO 22301', 'DORA'],
    lastReviewed: '2025-06-10',
    nextReview: '2026-06-10',
  },
];

export function PlaybooksPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPlaybook, setExpandedPlaybook] = useState<string | null>(null);

  // Filter playbooks
  const filteredPlaybooks = mockPlaybooks.filter((playbook) => {
    const matchesCategory = selectedCategory === 'All' || playbook.category === selectedCategory;
    const matchesSeverity = selectedSeverity === 'All' || playbook.severity === selectedSeverity;
    const matchesSearch =
      searchQuery === '' ||
      playbook.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      playbook.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSeverity && matchesSearch;
  });

  // Stats
  const totalPlaybooks = mockPlaybooks.length;
  const activePlaybooks = mockPlaybooks.filter((p) => p.status === 'Active').length;
  const totalActions = mockPlaybooks.reduce((sum, p) => sum + p.actions.length, 0);
  const criticalPlaybooks = mockPlaybooks.filter((p) => p.severity === 'Critical').length;

  // Get severity badge color
  const getSeverityBadgeColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'badge-danger';
      case 'High':
        return 'badge-warning';
      case 'Medium':
        return 'badge-primary';
      case 'Low':
        return 'badge-success';
      default:
        return 'badge';
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'badge-success';
      case 'In Progress':
        return 'badge-primary';
      case 'Not Started':
        return 'badge-warning';
      case 'Blocked':
        return 'badge-danger';
      default:
        return 'badge';
    }
  };
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="playbooks"
        icon={BookOpen}
        title="Scenario Playbooks"
        description="Pre-defined response procedures with actions for people and third parties"
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Scenario Playbooks</h1>
          <p className="text-neutral-400 mt-2">
            Pre-defined response procedures with actions for people and third parties
          </p>
        </div>
        <button className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create Playbook
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <BookOpen className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Total Playbooks</p>
              <p className="text-2xl font-bold text-white">{totalPlaybooks}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-success-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-success-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Active</p>
              <p className="text-2xl font-bold text-white">{activePlaybooks}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Total Actions</p>
              <p className="text-2xl font-bold text-white">{totalActions}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-danger-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-danger-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Critical Severity</p>
              <p className="text-2xl font-bold text-white">{criticalPlaybooks}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search playbooks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-input pl-10 w-full"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="form-input pl-10 w-full appearance-none"
              >
                <option value="All">All Categories</option>
                <option value="Cyber Security">Cyber Security</option>
                <option value="Data Breach">Data Breach</option>
                <option value="System Outage">System Outage</option>
                <option value="Compliance">Compliance</option>
                <option value="Third-Party">Third-Party</option>
                <option value="Business Continuity">Business Continuity</option>
              </select>
            </div>

            {/* Severity Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="form-input pl-10 w-full appearance-none"
              >
                <option value="All">All Severities</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Playbooks List */}
      <div className="space-y-4">
        {filteredPlaybooks.map((playbook) => (
          <div key={playbook.id} className="card">
            <div
              className="p-6 cursor-pointer hover:bg-neutral-900/50 transition-colors"
              onClick={() => setExpandedPlaybook(expandedPlaybook === playbook.id ? null : playbook.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-white">{playbook.name}</h3>
                    <span className={`badge ${getSeverityBadgeColor(playbook.severity)}`}>
                      {playbook.severity}
                    </span>
                    <span className="badge badge-primary">{playbook.category}</span>
                  </div>
                  <p className="text-neutral-400 mb-4">{playbook.description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-neutral-500">Owner</p>
                      <p className="text-sm text-white">{playbook.owner}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Actions</p>
                      <p className="text-sm text-white">{playbook.actions.length} steps</p>
                    </div>
                    {playbook.lastActivated && (
                      <div>
                        <p className="text-xs text-neutral-500">Last Activated</p>
                        <p className="text-sm text-white">{playbook.lastActivated}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-neutral-500">Avg Response Time</p>
                      <p className="text-sm text-white">{playbook.avgResponseTime}</p>
                    </div>
                  </div>
                </div>
                
                <button className="text-primary-500 hover:text-primary-400">
                  <ExternalLink className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Expanded Action Steps */}
            {expandedPlaybook === playbook.id && (
              <div className="border-t border-neutral-800">
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Action Steps</h4>
                  <div className="space-y-3">
                    {playbook.actions.map((action) => (
                      <div
                        key={action.id}
                        className="bg-neutral-900 rounded-lg p-4 border border-neutral-800"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500/20 text-primary-500 font-semibold text-sm">
                              {action.step}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-white">{action.title}</h5>
                                <span className={`badge ${getSeverityBadgeColor(action.priority)}`}>
                                  {action.priority}
                                </span>
                                <span className={`badge ${getStatusBadgeColor(action.status)}`}>
                                  {action.status}
                                </span>
                              </div>
                              <p className="text-sm text-neutral-400 mb-3">{action.description}</p>
                              
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                  {action.assigneeType === 'Internal' ? (
                                    <UserCheck className="w-4 h-4 text-primary-500" />
                                  ) : (
                                    <ShoppingCart className="w-4 h-4 text-warning-500" />
                                  )}
                                  <div>
                                    <p className="text-xs text-neutral-500">Assigned To</p>
                                    <p className="text-white">{action.assignedTo}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-neutral-400" />
                                  <div>
                                    <p className="text-xs text-neutral-500">Duration</p>
                                    <p className="text-white">{action.estimatedDuration}</p>
                                  </div>
                                </div>
                                {action.dependencies && action.dependencies.length > 0 && (
                                  <div>
                                    <p className="text-xs text-neutral-500">Dependencies</p>
                                    <p className="text-white text-xs">{action.dependencies.join(', ')}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Connected Entities */}
                  <div className="mt-6 pt-6 border-t border-neutral-800">
                    <h4 className="text-lg font-semibold text-white mb-4">Connected Entities</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {playbook.connectedProcesses.length > 0 && (
                        <div>
                          <p className="text-xs text-neutral-500 mb-2">Processes</p>
                          <div className="space-y-1">
                            {playbook.connectedProcesses.map((proc, idx) => (
                              <p key={idx} className="text-sm text-white">{proc}</p>
                            ))}
                          </div>
                        </div>
                      )}
                      {playbook.connectedRisks.length > 0 && (
                        <div>
                          <p className="text-xs text-neutral-500 mb-2">Risks</p>
                          <div className="space-y-1">
                            {playbook.connectedRisks.map((risk, idx) => (
                              <p key={idx} className="text-sm text-white">{risk}</p>
                            ))}
                          </div>
                        </div>
                      )}
                      {playbook.connectedSuppliers.length > 0 && (
                        <div>
                          <p className="text-xs text-neutral-500 mb-2">Third Parties</p>
                          <div className="space-y-1">
                            {playbook.connectedSuppliers.map((supplier, idx) => (
                              <p key={idx} className="text-sm text-white">{supplier}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
